import 'mapbox-gl/dist/mapbox-gl.css';

import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PlaceIcon from '@mui/icons-material/Place';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Map, { Marker } from 'react-map-gl';
import { useMediaQuery } from 'react-responsive';

import strapiApi from '../../api/strapi';
import CustomButton from '../../components/CustomButton';
import Member from '../../components/member/index';
import Metatags from '../../components/MetaTags/metatags';
import { COLORS, PATHS } from '../../constants';
import fontsStyles from '../../styles/fontStyles';
import styles from './styles';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Grow timeout={{ appear: 5000, exit: 3000 }} ref={ref} {...props} />;
});

const Header = () => {
  const classes = styles();
  const fontClasses = fontsStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.afiliateWrapper}>
      <Box className={classes.afiliateontentWrapper}>
        <Box>
          <p className={fontClasses.sectionSubTitles}>{t('directoryPage.headerSubTitle')}</p>
          <h1 className={clsx(fontClasses.h1, classes.sectionTitle)}>
            {t('directoryPage.headerTitle')}
          </h1>
          <p className={clsx(fontClasses.p, classes.sectionText)}>
            {t('directoryPage.headerText')}
          </p>
          <Box className={classes.buttonsBox}>
            <CustomButton label={t('directoryPage.afiliate')} url={PATHS.affiliation} />
            <CustomButton
              label={t('directoryPage.contact')}
              url={PATHS.contact}
              type={'secondary'}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ContentModal = ({ member, pModalOpen, pHandleClose, isMobile, mapboxKey }) => {
  const classes = styles();
  const fontClasses = fontsStyles();
  const { t } = useTranslation();

  return (
    <Dialog
      TransitionComponent={Transition}
      open={pModalOpen}
      onClose={pHandleClose}
      fullWidth
      sx={{ zIndex: 1000, borderRadius: '0px' }}
      maxWidth='md'
      fullScreen={!isMobile}
    >
      <IconButton className={classes.modalCloseIcon} size='medium' onClick={pHandleClose}>
        <CloseIcon sx={{ fontSize: '25px', color: COLORS.gray }} />
      </IconButton>
      <Box className={classes.coverImgBox}>
        <img src={member.cover?.url} className={classes.coverImg} />
      </Box>
      <Box className={classes.modalBox}>
        <Box className={classes.modalTitleBox}>
          <h3 className={fontClasses.h3} style={{ marginBottom: '5px' }}>
            {member.name}
          </h3>
          <Chip
            label={
              member.type === 'Emprendedora'
                ? t('directoryPage.entrepreneur')
                : t('directoryPage.businessWoman')
            }
            sx={{ marginBottom: '15px' }}
          />
          <p className={fontClasses.p}>{member.resume}</p>
        </Box>
        <Box>
          {member.schedule && (
            <Box className={classes.modalItemContent}>
              <ScheduleIcon sx={{ color: COLORS.gray, fontSize: '25px' }} />
              <Box>
                <p className={fontClasses.p}>
                  <b>{t('directoryPage.schedule')}:</b>
                </p>
                <p className={fontClasses.p}>{member.schedule}</p>
              </Box>
            </Box>
          )}
          {member.telephone && (
            <Box className={classes.modalItemContent}>
              <LocalPhoneIcon sx={{ color: COLORS.gray, fontSize: '25px' }} />
              <Box>
                <p className={fontClasses.p}>
                  <b>{t('directoryPage.phone')}:</b>
                </p>
                <p className={fontClasses.p}>
                  <a
                    href={`tel:${member.telephone}`}
                    style={{ textDecoration: 'none', color: COLORS.gray }}
                  >
                    {member.telephone}
                  </a>
                </p>
              </Box>
            </Box>
          )}
          {member.email && (
            <Box className={classes.modalItemContent}>
              <EmailIcon sx={{ color: COLORS.gray, fontSize: '25px' }} />
              <Box>
                <p className={fontClasses.p}>
                  <b>{t('directoryPage.email')}:</b>
                </p>
                <p className={fontClasses.p}>
                  <a
                    href={`mailto:${member.email}`}
                    style={{ textDecoration: 'none', color: COLORS.gray }}
                  >
                    {member.email}
                  </a>
                </p>
              </Box>
            </Box>
          )}

          {member.address && (
            <Box className={classes.modalItemContent}>
              <LocationOnIcon sx={{ color: COLORS.gray, fontSize: '25px' }} />
              <Box>
                <p className={fontClasses.p}>
                  <b>{t('directoryPage.address')}:</b>
                </p>
                <p className={fontClasses.p}>{member.address}</p>
              </Box>
            </Box>
          )}
          {member.address_coords && (
            <Box>
              <Map
                mapboxAccessToken={mapboxKey}
                initialViewState={{
                  longitude: member.address_coords.longitude,
                  latitude: member.address_coords.latitude,
                  zoom: 14,
                }}
                style={{ width: '100%', height: '250px', borderRadius: '10px' }}
                mapStyle='mapbox://styles/jeanvegad/clnfkgnl1083001qx6dbdg9r7'
                attributionControl={false}
                logoPosition='bottom-right'
              >
                <Marker
                  longitude={member.address_coords.longitude}
                  latitude={member.address_coords.latitude}
                  anchor='bottom'
                >
                  <PlaceIcon style={{ fontSize: '40px', color: COLORS.cyan }} />
                </Marker>
              </Map>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

const DirectoryContent = ({ language, isMobile, mapboxKey }) => {
  const classes = styles();
  const fontClasses = fontsStyles();
  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState('E');
  const [directoryList, setDirectoryList] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, pageCount: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const delay = setTimeout(() => {
      const fetchMembers = async () => {
        setLoading(true);
        setError(false);

        try {
          const response = await strapiApi.get('/members', {
            params: {
              'filters[name][$containsi]': searchValue,
              'filters[type][$contains]': filter,
              'pagination[page]': searchValue ? 1 : currentPage,
              'pagination[pageSize]': 12,
              sort: 'name:asc',
              locale: language,
              'populate[logo][fields][0]': 'url',
              'populate[cover][fields][0]': 'url',
            },
          });

          setDirectoryList(response.data.data);
          setPagination(response.data.meta.pagination);
        } catch (err) {
          setError(true);
        } finally {
          setLoading(false);
        }
      };

      fetchMembers();
    }, 400);

    return () => clearTimeout(delay);
  }, [searchValue, filter, currentPage, language]);

  const handleClose = () => {
    setModalOpen(false);
    setSelectedMember(null);
  };

  const handleClickMember = (member) => {
    setSelectedMember(member);
    setModalOpen(true);
  };

  const handleChangeFilter = (event) => {
    setFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleChangePage = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  return (
    <>
      <Box className={classes.seminarsListWrapper}>
        <Box className={classes.filtersWrapper}>
          <Box className={classes.searchBox}>
            <TextField
              key='search'
              name='search'
              fullWidth
              className={classes.inputText}
              variant='outlined'
              placeholder={t('directoryPage.search')}
              value={searchValue}
              onChange={(e) => {
                e.preventDefault();
                setSearchValue(e.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon style={{ color: COLORS.gray }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box className={classes.filtersBox}>
            <Select
              autoWidth
              value={filter}
              onChange={handleChangeFilter}
              startAdornment={
                <InputAdornment position='start'>
                  <ImportExportIcon style={{ color: COLORS.gray }} />
                  <p style={{ color: COLORS.gray }}>{t('directoryPage.filter')}:</p>
                </InputAdornment>
              }
              className={classes.select}
            >
              <MenuItem value={'E'}>
                <p className={fontClasses.p}>{t('directoryPage.all')}</p>
              </MenuItem>
              <MenuItem value={'Empresaria'}>
                <p className={fontClasses.p}>{t('directoryPage.businessWoman')}</p>
              </MenuItem>
              <MenuItem value={'Emprendedora'}>
                <p className={fontClasses.p}>{t('directoryPage.entrepreneur')}</p>
              </MenuItem>
            </Select>
          </Box>
        </Box>

        <Box className={classes.seminarsListContentWrapper}>
          {loading && !error && (
            <>
              <Skeleton
                variant='rectangular'
                height={400}
                sx={{ borderRadius: '10px', width: { xs: '100%', sm: '100%', md: 350, lg: 350 } }}
              />
              <Skeleton
                variant='rectangular'
                height={400}
                sx={{ borderRadius: '10px', width: { xs: '100%', sm: '100%', md: 350, lg: 350 } }}
              />
              <Skeleton
                variant='rectangular'
                height={400}
                sx={{ borderRadius: '10px', width: { xs: '100%', sm: '100%', md: 350, lg: 350 } }}
              />
            </>
          )}
          {directoryList &&
            !error &&
            !loading &&
            directoryList.map((member, index) => (
              <Box key={member.documentId ?? index}>
                <Member
                  clickFunction={() => handleClickMember(member)}
                  name={member.name}
                  cover={member.cover?.url}
                  logo={member.logo?.url}
                  resume={member.resume}
                />
              </Box>
            ))}
        </Box>

        {!loading && !directoryList && error && (
          <Box className={classes.errorBox}>
            <InfoOutlinedIcon sx={{ color: COLORS.gray, fontSize: '100px' }} />
            <Box className={classes.errorBoxInfo}>
              <h4 className={fontClasses.h4}>{t('validationsForm.errorTitle')}</h4>
              <p className={fontClasses.p}>{t('validationsForm.errorMessage')}</p>
            </Box>
          </Box>
        )}

        {!loading && !error && directoryList && pagination.total === 0 && (
          <Box className={classes.errorBox}>
            <InfoOutlinedIcon sx={{ color: COLORS.gray, fontSize: '100px' }} />
            <Box className={classes.errorBoxInfo}>
              <h4 className={fontClasses.h4}>{t('validationsForm.notFound')}</h4>
              <p className={fontClasses.p}>{t('validationsForm.notFoundMessage')}</p>
            </Box>
          </Box>
        )}

        {directoryList && !error && !loading && pagination.total > 0 && (
          <Box className={classes.paginationBox}>
            <Pagination
              count={pagination.pageCount}
              page={currentPage}
              onChange={handleChangePage}
            />
          </Box>
        )}
      </Box>

      {selectedMember && (
        <ContentModal
          member={selectedMember}
          pModalOpen={modalOpen}
          pHandleClose={handleClose}
          isMobile={isMobile}
          mapboxKey={mapboxKey}
        />
      )}
    </>
  );
};

const Directory = () => {
  const { i18n } = useTranslation();
  const isMobile = useMediaQuery({ query: '(min-width: 800px)' });
  const mapboxKey = process.env.REACT_APP_MAPBOX_TOKEN;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <Box>
      <Metatags
        title='CEMCA | Directorio de afiliadas'
        description='Visite nuestro directorio de empresas afiliadas.'
      />
      <Header />
      <DirectoryContent language={i18n.language} isMobile={isMobile} mapboxKey={mapboxKey} />
    </Box>
  );
};

export default React.memo(Directory);
